/**
 * The consent checklist's pure parts.
 *
 * Separate from the page so they can be pinned under Node: the sentence that
 * tells a person which books an assistant will see is the one line on
 * that screen that must never be wrong, and a component test cannot run
 * without a DOM while this can.
 */

/**
 * The books to start with ticked.
 *
 * Exactly one membership: ticked, because there is no choice to make and an
 * unticked list of one trains people to tick the first thing they see. More
 * than one: NOTHING ticked. The active org used to be pre-selected; under a
 * multi-select that is a default the eye reads as "already decided", and a
 * connection to the wrong client's books is found out when the assistant
 * answers about the wrong business, confidently.
 */
export function initialSelection(orgIds: string[]): string[] {
  return orgIds.length === 1 ? [...orgIds] : [];
}

/** Add or remove one id. */
export function toggle(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
}

/**
 * "A", "A and B", "A, B and C". A list, in prose, because it is going into a
 * sentence a person is about to agree to and "A, B, C" reads as a table.
 */
export function listNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A role in words. The API sends `admin` or `member` for the built-in roles
 * and the role's own name for a custom one; the first two get their proper
 * nouns and anything else is already a name.
 */
export function roleLabel(role: string): string {
  if (role === 'admin') return 'Administrator';
  if (role === 'member') return 'Member';
  return role;
}

/** The approve button's label: which, or how many. */
export function approveLabel(selectedNames: string[]): string {
  if (selectedNames.length === 0) return 'Connect';
  if (selectedNames.length === 1) return `Connect to ${selectedNames[0]}`;
  return `Connect ${selectedNames.length} books`;
}
